<script setup lang="ts">
import InputWrapper from "@/stories/form/InputWrapper/InputWrapper.vue";
import { vMaska } from "maska/vue";
import { nanoid } from "nanoid";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  type StyleValue,
} from "vue";
import { watch } from "vue";
import { onClickOutside } from "@vueuse/core";
import { toRefs } from "vue";
import { reactive } from "vue";
import { isValid, parse, format as formatTime } from "date-fns";
import type { DefaultInputProps, ExtendedItem } from "@/interfaces";
import type { HourFormatTypes } from "@/types";
import { useFieldValidationError } from "@/composables/useFieldValidationError";
import TimeItem from "@/stories/form/TimePicker/TimeItem.vue";
import TimeList from "@/stories/form/TimePicker/TimeList.vue";
import { convertTo12Hour, getTimes } from "@/stores/helpers";
import { useElementRect } from "@/composables/useElementRect";
import pixelToRem from "@/helpers/shared/pixelToRem";

const id = nanoid();

interface Props extends Omit<DefaultInputProps, "label"> {
  label?: string;
  format?: HourFormatTypes;
  modelValue: string;
  hideValidationError?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  format: (navigator.language || "da") === "en-GB" ? "12h" : "24h",
  hideValidationError: false,
});

const emit = defineEmits(["update:modelValue"]);

const { format, modelValue, validation, name } = toRefs(props);

const isListOpen = ref(false);
const buttonRef = ref();
const listRef = ref();
const inputRef = ref();
const fieldRef = ref();
const fieldWidth = ref(0);
const resizeObserver = ref();
const searchText = ref<string | undefined>("");
const rect = useElementRect(inputRef);

const items = ref<ExtendedItem[]>(getTimes(format.value));

const setSearchText = (value: string) => {
  if (!value) {
    searchText.value = "";
    return;
  }

  if (format.value === "12h") {
    searchText.value = convertTo12Hour(value);
    return;
  }

  searchText.value = value;
};

watch(
  () => modelValue.value,
  () => {
    setSearchText(modelValue.value);
  },
);

watch(
  () => format.value,
  () => {
    items.value = getTimes(format.value);
  },
);

watch(
  () => modelValue.value,
  () => {
    setSearchText(modelValue.value);
  },
);

onMounted(() => {
  setSearchText(modelValue.value);

  if (fieldRef.value) {
    const element = fieldRef.value;

    resizeObserver.value = new ResizeObserver(function () {
      fieldWidth.value = element.clientWidth;
    });

    fieldWidth.value = element.clientWidth;
    resizeObserver.value.observe(element);
  }
});

onBeforeUnmount(() => {
  if (fieldRef.value && resizeObserver.value) {
    const element = fieldRef.value;
    resizeObserver.value.unobserve(element);
  }
});

const closeList = () => {
  isListOpen.value = false;
};

const handleUpdate = () => {
  closeList();

  const dateObject = convertToDateObect(searchText.value?.toString() || "");

  // Checking if it is a valid date
  if (isValid(dateObject) === false) {
    searchText.value = "";
    handleInput("");
    return;
  }

  // Updating the model value
  handleInput(formatTime(dateObject, "HH:mm"));
};

const handleInternalClick = (item: string | undefined) => {
  searchText.value = item;
  handleUpdate();
  closeList();
};

onClickOutside(listRef, closeList, { ignore: [buttonRef] });

const options = reactive({
  mask: format.value === "12h" ? "##:##Jm" : "##:##",
  eager: true,
  tokens: { J: { pattern: /[a|p]/ } },
});

const convertToDateObect = (value: string) => {
  let dateObject;
  // Parsing the date by the format
  if (format.value === "12h") {
    dateObject = parse(value.toUpperCase(), "hh:mma", new Date());
  } else {
    dateObject = parse(value.toUpperCase(), "HH:mm", new Date());
  }

  return dateObject;
};

const handleInputLeave = () => {
  let value: string = searchText.value?.toString() || "";

  if (format.value === "12h" && !value.includes("m")) {
    value += "pm";
  }

  const dateObject = convertToDateObect(value);

  // Checking if it is a valid date
  if (isValid(dateObject) === false) {
    searchText.value = "";
    handleInput("");
    return;
  }

  // Updating the model value
  handleInput(formatTime(dateObject, "HH:mm"));
};

const handleInputClick = () => {
  isListOpen.value = !isListOpen.value;
  inputRef.value.select();
};

const handleInputKeypress = (event: KeyboardEvent) => {
  if (event.key === "Enter") {
    closeList();
    handleInputLeave();
  } else if (isListOpen.value === false) {
    isListOpen.value = true;
  }
};

const handleInput = (value: string) => {
  emit("update:modelValue", value);

  if (validation.value && validation.value.errors) {
    delete validation.value.errors[name.value];
  }
};

const { hasError } = useFieldValidationError(name, validation);

const customStyle = computed(() => {
  const styles: Partial<StyleValue> = {};

  const inputHeight: number = inputRef.value.getBoundingClientRect().height;

  const top = Math.round(rect.value?.top || 0) + inputHeight;

  styles.top = pixelToRem(top);

  const width = rect.value?.width ? Math.round(rect.value.width) : undefined;
  if (width) {
    styles.width = pixelToRem(width);
  }

  const possibleBottomSpace = window.innerHeight - top - 20;
  // If there is not any space more than 12.5rem, we should display the list on
  // the top instead of bottom.
  if (possibleBottomSpace < 200) {
    styles.top = pixelToRem(top - 400 - inputHeight);
    // We should add maxHeight
    styles.maxHeight = "25rem";
  } else if (possibleBottomSpace < 500) {
    styles.maxHeight = pixelToRem(possibleBottomSpace - 20);
  } else {
    styles.maxHeight = `31.25rem`;
  }

  return styles;
});
</script>

<template>
  <InputWrapper
    :forId="id"
    :label="label"
    :description="description"
    :isRequired="isRequired"
    :inputKey="inputKey"
    :tooltip="tooltip"
    :disabled="disabled"
    :name="name"
    :validation="validation"
    :errorMessageKey="errorMessageKey"
    :hide-validation-error="hideValidationError"
  >
    <div class="relative" ref="fieldRef">
      <input
        ref="inputRef"
        type="tel"
        :id="id"
        :disabled="disabled"
        class="transition duration-200 text-base w-full text-neutral1000 border border-neutral500 placeholder:text-neutral600 placeholder:font-normal placeholder:tracking-tight focus:outline-2 focus:enabled:outline-neutral1000 disabled:text-neutral600 input focusVisible px-3 py-3 b300 rounded-lg disabled:bg-neutral50 h-[2.5rem]"
        :class="{
          'outline outline-2 outline-red400': hasError,
          'hover:enabled:outline-neutral500': !hasError,
        }"
        v-model="searchText"
        @click="handleInputClick"
        @blur="handleInputLeave"
        @keyup="handleInputKeypress"
        v-maska="options"
      />
      <i
        class="absolute right-3 text-lg top-[0.4rem] fa fa-chevron-down pointer-events-none"
        :class="{ 'text-neutral600': disabled, 'text-neutral1000': !disabled }"
      ></i>
      <TimeList
        v-if="isListOpen"
        :fieldWidth="undefined"
        ref="listRef"
        label="Synlig fra"
        :groupId="id"
        :modelValue="modelValue"
        :searchText="searchText || ''"
        :customStyle="customStyle"
        @close="closeList"
        @updateInternal="handleUpdate"
      >
        <TimeItem
          v-for="(item, index) of items"
          :key="index"
          @click="handleInternalClick(item.value)"
          :model="searchText || ''"
          :item="item"
          :groupId="id"
          :enableIcon="false"
        />
      </TimeList>
    </div>
  </InputWrapper>
</template>
